import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { apiService } from '../services/apiService';

type AlertRow = {
  type: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH';
  title?: string;
  createdAt?: string;
  details?: any;
};

function severityColor(sev: string): 'default' | 'warning' | 'error' | 'success' | 'info' {
  if (sev === 'HIGH') return 'error';
  if (sev === 'MEDIUM') return 'warning';
  return 'default';
}

const SuperAdminAlertsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<AlertRow[]>([]);

  const [windowHours, setWindowHours] = useState('24');
  const [typeFilter, setTypeFilter] = useState('');

  const query = useMemo(() => {
    const h = Number(windowHours);
    return { windowHours: Number.isFinite(h) ? h : 24 };
  }, [windowHours]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getAdminAlerts(query);
      setRows(Array.isArray(res) ? res : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [query.windowHours]);

  const filtered = useMemo(() => {
    if (!typeFilter) return rows;
    return rows.filter((r) => r.type === typeFilter);
  }, [rows, typeFilter]);

  const types = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => set.add(r.type));
    return Array.from(set.values()).sort();
  }, [rows]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <NotificationsActiveIcon />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Alerts &amp; Notifications
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            select
            label="Window"
            value={windowHours}
            onChange={(e) => setWindowHours(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="6">Last 6 hours</MenuItem>
            <MenuItem value="24">Last 24 hours</MenuItem>
            <MenuItem value="72">Last 3 days</MenuItem>
            <MenuItem value="168">Last 7 days</MenuItem>
          </TextField>
          <TextField
            size="small"
            select
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All</MenuItem>
            {types.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Paper>

      {loading && <CircularProgress size={22} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((r, idx) => (
              <TableRow key={`${r.type}-${idx}`}>
                <TableCell>{r.createdAt || '-'}</TableCell>
                <TableCell>
                  <Chip size="small" label={r.severity} color={severityColor(r.severity)} />
                </TableCell>
                <TableCell>{r.type}</TableCell>
                <TableCell>{r.title || '-'}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                    {r.details ? JSON.stringify(r.details) : '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default SuperAdminAlertsPage;

